/**
 * Helpers de upload ARCA PDF — itens com miniatura (PDF 1ª página / imagem).
 */
import { PDFDocument, degrees } from 'pdf-lib';

const PDF_JS_SCRIPT = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
const PDF_JS_WORKER = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

function carregarPdfJs() {
  if (typeof window === 'undefined') return Promise.reject(new Error('Sem window'));
  if (window.pdfjsLib) {
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_JS_WORKER;
    return Promise.resolve(window.pdfjsLib);
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PDF_JS_SCRIPT;
    script.async = true;
    script.onload = () => {
      if (!window.pdfjsLib) {
        reject(new Error('pdf.js não carregou.'));
        return;
      }
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_JS_WORKER;
      resolve(window.pdfjsLib);
    };
    script.onerror = () => reject(new Error('Falha ao carregar pdf.js.'));
    document.head.appendChild(script);
  });
}

export function newUploadItemId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function isPdfFile(file) {
  return file && (file.type === 'application/pdf' || /\.pdf$/i.test(file.name || ''));
}

export function isImageFile(file) {
  return (
    file &&
    (/^image\/(jpeg|jpg|png|webp|gif)$/i.test(file.type || '') ||
      /\.(jpe?g|png|webp|gif)$/i.test(file.name || ''))
  );
}

async function thumbnailFromPdf(file) {
  const pdfjs = await carregarPdfJs();
  const data = new Uint8Array(await file.arrayBuffer());
  const pdf = await pdfjs.getDocument({ data }).promise;
  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale: 0.35 });
  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
  return canvas.toDataURL('image/jpeg', 0.82);
}

async function thumbnailFromImage(file) {
  return URL.createObjectURL(file);
}

/**
 * @param {File} file
 */
export function createUploadItem(file) {
  return {
    id: newUploadItemId(),
    file,
    thumbnailUrl: null,
    thumbLoading: true,
    thumbError: false,
  };
}

/**
 * Gira todas as páginas do PDF +90° (horário) e devolve um novo File.
 * @param {File} file
 * @returns {Promise<File>}
 */
export async function rotatePdfFile90(file) {
  if (!isPdfFile(file)) throw new Error('Somente PDF pode ser girado.');
  const bytes = await file.arrayBuffer();
  const pdf = await PDFDocument.load(bytes, { ignoreEncryption: true });
  pdf.getPages().forEach((page) => {
    const current = page.getRotation()?.angle || 0;
    page.setRotation(degrees((((current + 90) % 360) + 360) % 360));
  });
  const out = await pdf.save();
  return new File([out], file.name, {
    type: 'application/pdf',
    lastModified: Date.now(),
  });
}

/**
 * Gera thumbnail async e devolve item atualizado (não muta o original).
 */
export async function generateUploadThumbnail(item) {
  try {
    let thumbnailUrl = null;
    if (isPdfFile(item.file)) {
      thumbnailUrl = await thumbnailFromPdf(item.file);
    } else if (isImageFile(item.file)) {
      thumbnailUrl = await thumbnailFromImage(item.file);
    }
    return {
      ...item,
      thumbnailUrl,
      thumbLoading: false,
      thumbError: !thumbnailUrl,
    };
  } catch {
    return {
      ...item,
      thumbnailUrl: null,
      thumbLoading: false,
      thumbError: true,
    };
  }
}

/**
 * Adiciona arquivos à lista e dispara geração de thumbs via setItems.
 */
export function appendUploadFiles(files, setItems, opts = {}) {
  const { multiple = true, filter } = opts;
  let list = Array.from(files || []);
  if (typeof filter === 'function') list = list.filter(filter);
  if (!list.length) return 0;
  if (!multiple) list = list.slice(0, 1);

  const created = list.map((file) => createUploadItem(file));

  setItems((prev) => {
    const next = multiple ? [...prev, ...created] : created;
    return next;
  });

  created.forEach((item) => {
    generateUploadThumbnail(item).then((updated) => {
      setItems((prev) => prev.map((p) => (p.id === item.id ? { ...p, ...updated } : p)));
    });
  });

  return created.length;
}

/**
 * Substitui o File do item (ex.: após girar) e regenera thumbnail.
 */
export function replaceUploadItemFile(itemId, newFile, setItems) {
  setItems((prev) =>
    prev.map((p) =>
      p.id === itemId
        ? {
            ...p,
            file: newFile,
            thumbnailUrl: null,
            thumbLoading: true,
            thumbError: false,
          }
        : p
    )
  );
  const stub = { id: itemId, file: newFile };
  generateUploadThumbnail(stub).then((updated) => {
    setItems((prev) =>
      prev.map((p) => (p.id === itemId ? { ...p, ...updated, file: newFile } : p))
    );
  });
}
